"use client"

import { heIL } from "@mui/material/locale"
import { createTheme, ThemeOptions } from "@mui/material/styles"
import boxShadows from "./boxShadows"
import colors from "./colors"

// Extend MUI's breakpoint types to include custom breakpoints
declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true
    sm: true
    md: true
    smallLaptop: true // Add custom breakpoint
    mediumLaptop: true // Add custom breakpoint
    lg: true
    xl: true
  }
}

// --- You can't do prop-based logic in theme anymore ---
// If you still want Hebrew/Latin logic per Typography,
// create a custom <MazdaTypography> component and move
// getFontStyle / getFontStyleNumber logic there.
export const getFontStyle = (
  text: string,
  hebStyle: string,
  engStyle: string
): string => {
  const latinChars = /[a-zA-Z0-9]/
  if (text) {
    if (latinChars.test(text)) {
      return engStyle
    } else {
      return hebStyle
    }
  }
  return engStyle
}

export const getFontStyleNumber = (
  text: string,
  hebStyle: number,
  engStyle: number
): number => {
  const latinChars = /[a-zA-Z0-9]/
  if (text) {
    if (latinChars.test(text)) {
      return engStyle
    } else {
      return hebStyle
    }
  }
  return engStyle
}

const muiThemeOptions: ThemeOptions = {
  direction: "rtl",
  spacing: 3,

  // keep old v4 breakpoints so layout doesn’t shift
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      smallLaptop: 1080,
      mediumLaptop: 1440,
      lg: 1280,
      xl: 1920,
    },
  },

  // --------------------------------------------------
  // Typography
  // --------------------------------------------------
  typography: {
    fontFamily: "MazdaType, OENaPro, sans-serif;",
    h1: {
      fontSize: "28px",
      fontWeight: 500,
      lineHeight: "140%",
      color: colors.black,
      letterSpacing: "0.09em",
    },
    h2: {
      fontSize: "24px",
      fontWeight: 400,
      lineHeight: "140%",
      color: colors.black,
      letterSpacing: "0.05em",
    },
    h3: {
      fontSize: "20px",
      fontWeight: 300,
      lineHeight: "160%",
      color: colors.black,
      letterSpacing: "0.03em",
    },
    subtitle1: {
      fontSize: "20px",
      fontWeight: 300,
      lineHeight: "160%",
      color: colors.black,
      letterSpacing: "0.03em",
    },
    body1: {
      fontSize: "18px",
      fontWeight: 300,
      lineHeight: "150%",
      letterSpacing: "0",
      color: colors.black,
    },
    body2: {
      fontSize: "16px",
      fontWeight: 300,
      lineHeight: "140%",
      letterSpacing: "0",
      color: colors.black,
    },
    caption: {
      fontSize: "12px",
      fontWeight: 300,
      lineHeight: "130%",
      letterSpacing: "0",
      color: colors.black,
    },
    button: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "140%",
      letterSpacing: "0.05em",
      color: colors.black,
    },
  },

  // --------------------------------------------------
  // PALETTE
  // --------------------------------------------------
  palette: {
    background: {
      default: colors.white,
    },
    primary: {
      main: colors.black,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.white,
      contrastText: colors.black,
    },
    text: {
      primary: colors.black,
    },
    error: {
      main: "#b50303",
    },
  },

  // --------------------------------------------------
  // COMPONENTS (props + overrides)
  // --------------------------------------------------
  components: {
    // ---- Defaults (old `props`) ----
    MuiContainer: {
      defaultProps: {
        maxWidth: "lg",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          paddingRight: "15px",
          paddingLeft: "15px",
          [theme.breakpoints.up("sm")]: {
            paddingRight: "15px",
            paddingLeft: "15px",
          },
        }),
      },
    },
    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "16px",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        variant: "outlined",
        autoComplete: "true",
      },
    },
    MuiButton: {
      defaultProps: {
        disableTouchRipple: true,
      },
      styleOverrides: {
        text: {
          minWidth: "120px",
          fontSize: "16px",
          width: "fit-content",
          color: "#FFFFFF",
          backgroundColor: "#101010",
          border: `2px solid #101010`,
          padding: "11.5px 13px",
          "&:hover": {
            boxShadow: "none",
            backgroundColor: "#3A3D46",
            color: "#FFFFFF",
            border: `2px solid #3A3D46`,
          },
        },
        textSecondary: {
          minWidth: "120px",
          fontSize: "16px",
          width: "fit-content",
          color: "#101010",
          backgroundColor: "#FFFFFF",
          border: `2px solid #FFFFFF`,
          padding: "11.5px 13px",
          "&:hover": {
            boxShadow: "none",
            backgroundColor: "#D5D5D5",
            color: "#101010",
            border: `2px solid #D5D5D5`,
          },
        },
        outlined: {
          minWidth: "120px",
          fontSize: "16px",
          width: "fit-content",
          color: "#101010",
          backgroundColor: "transparent",
          border: `2px solid #101010`,
          padding: "11.5px 13px",
          "&:hover": {
            boxShadow: "none",
            backgroundColor: "#3A3D46",
            color: "#FFFFFF",
            border: `2px solid #3A3D46`,
          },
        },
        outlinedSecondary: {
          minWidth: "120px",
          fontSize: "16px",
          width: "fit-content",
          color: "#FFFFFF",
          backgroundColor: "transparent",
          border: `2px solid #FFFFFF`,
          padding: "11.5px 13px",
          "&:hover": {
            boxShadow: "none",
            backgroundColor: "#D5D5D5 ",
            color: "#101010",
            border: `2px solid #D5D5D5`,
          },
        },
        // article underline button
        contained: {
          minWidth: "0px",
          fontSize: "16px",
          width: "fit-content",
          color: "#101010",
          backgroundColor: "transparent",
          border: "transparent",
          borderRadius: "0",
          borderBottom: "2px solid #101010",
          padding: "0px",
          margin: "4px 0px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
            backgroundColor: "transparent",
            border: "transparent",
            color: "#910A2D",
            borderBottom: "2px solid #910A2D",
          },
          "&:disabled": {
            boxShadow: "none",
            backgroundColor: "transparent",
            border: "transparent",
            borderRadius: "0",
            color: "#616161",
            borderBottom: "2px solid #910A2D",
          },
          "&:focus": {
            boxShadow: "none",
            backgroundColor: "transparent",
            border: "transparent",
            color: "#910A2D",
            borderBottom: "2px solid #910A2D",
          },
        },
        containedSecondary: {
          minWidth: "0px",
        },
        sizeSmall: {
          padding: "11.5px 13px",
          fontSize: "16px",
          fontWeight: 400,
          minHeight: "0px",
          minWidth: "0px",
          height: "fit-content",
        },
        root: {
          minWidth: "120px",
          fontSize: "16px",
          width: "fit-content",
          padding: "11.5px 13px",
          textAlign: "center",
          "&:hover": {
            boxShadow: "none",
            backgroundColor: "#3A3D46",
            color: "#FFFFFF",
            border: `2px solid #3A3D46`,
          },
          "&:disabled": {
            boxShadow: "none",
            backgroundColor: "#999999",
            color: "#FFFFFF",
            border: `2px solid #999999`,
          },
          "&:focus-visible": {
            border: "2px dashed white",
            outlineOffset: "2px",
            outline: "3px dashed #0e3487",
          },
        },
        startIcon: {
          marginLeft: "8px",
          marginRight: "-4px",
        },
      },
    },

    // ---- Typography OVERRIDES (static only) ----
    // Per-text dynamic logic via props.children is not supported.
    // Here, we keep just the base Typography in Typography.h1/h2/...
    // If you need dynamic per-language tweaks, do it in a wrapper component.

    // ---- PAPER ----
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          width: "fit-content",
          backgroundColor: colors.white,
          boxShadow: boxShadows.cardShadow,
          borderRadius: "3px",
          padding: "20px",
          overflow: "hidden",
        },
        elevation4: {
          boxShadow: "none",
        },
        rounded: {
          backgroundColor: "transparent",
        },
      },
    },

    // ---- DRAWER ----
    MuiDrawer: {
      styleOverrides: {
        paper: {
          paddingTop: "56px",
        },
      },
    },

    // ---- DIVIDER ----
    MuiDivider: {
      styleOverrides: {
        root: {
          height: "2px",
          width: "calc(100% - 60px)",
          margin: "0 auto",
        },
      },
    },

    // ---- LIST ITEM ----
    MuiListItem: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            border: "none",
            outline: "none",
          },
        },
        gutters: {
          paddingLeft: "27px",
          paddingRight: "0",
        },
      },
    },

    // ---- BACKDROP ----
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(16, 16, 16, 0.4);",
          WebkitBackdropFilter: "blur(5.5px)",
          backdropFilter: "blur(5.5px)",
        },
      },
    },

    // ---- DIALOG ----
    MuiDialog: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.continue-purchase-root": {
            padding: "10px 15px !important",
            [theme.breakpoints.down("sm")]: {
              padding: "10px !important",
            },
          },
        }),
        paperWidthSm: ({ theme }) => ({
          borderRadius: 30,
          overflow: "hidden",
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            maxHeight: "100%",
          },
        }),
        paperWidthXs: {
          maxWidth: "238px",
          "&.continue-purchase": {
            width: "340px",
            maxWidth: "340px",
          },
        },
        paperScrollPaper: {
          maxHeight: "calc(100% - 90px)",
          overflowY: "initial",
        },
        paper: {
          width: "100%",
          margin: "32px 6px;",
        },
        paperScrollBody: ({ theme }) => ({
          overflow: "visible",
          margin: "50px 6px;",
          [theme.breakpoints.down("md")]: {
            width: "calc(100% - 12px)",
            maxWidth: "100% !important",
          },
        }),
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          borderRadius: "5px",
          backgroundColor: colors.white,
        },
      },
    },

    // ---- SVG ICON ----
    MuiSvgIcon: {
      styleOverrides: {
        colorPrimary: {
          color: colors.black,
        },
      },
    },

    // ---- ACCORDION ----
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: "1.5px solid white",
          minHeight: "0px",
          boxShadow: "none",
          "&:not(:last-child)": {
            borderBottom: 0,
          },
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            margin: "auto",
          },
        },
        rounded: {
          "&:last-child": {
            borderBottomRightRadius: "16px",
            borderBottomLeftRadius: "16px",
            overflow: "hidden",
          },
          "&:first-child": {
            borderTopRightRadius: "16px",
            borderTopLeftRadius: "16px",
            overflow: "hidden",
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          flexWrap: "wrap",
          padding: "8px 15px",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: "0px",
          backgroundColor: colors.flour,
          padding: "0px",
          "&.Mui-expanded": {
            minHeight: "0px",
          },
        },
        content: {
          margin: "0px",
          padding: "14px 15px",
          alignItems: "center",
          "&.Mui-expanded": {
            margin: "0px",
            padding: "14px 15px",
            alignItems: "center",
          },
        },
        expandIconWrapper: {
          "&.Mui-expanded": {
            transform: "scaleY(-1)",
          },
        },
      },
    },

    // ---- INPUT ----
    MuiInput: {
      styleOverrides: {
        input: {
          borderRadius: 3,
          height: "41px",
          "&::placeholder": {
            textOverflow: "ellipsis !important",
            fontSize: 16,
          },
        },
        root: {
          "&.Mui-disabled": {
            color: colors.flour,
          },
        },
      },
    },

    // ---- OUTLINED INPUT ----
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          borderColor: "#999999",
          borderWidth: 0.5,
          padding: "11px 10px",
          fontSize: 16,
          height: "1.1876em",
          boxSizing: "content-box",
          // NOTE: // This was causing issues with build, so commented out for now.
          //   "&::placeholder": {
          //     textOverflow: "ellipsis !important",
          //     color: colors.marbleGrey,
          //     fontSize: 16,
          //   },
        },
        root: {
          borderRadius: 3,
          backgroundColor: colors.white,
          color: colors.black,
          fontSize: 16,
          borderColor: colors.darkGrey,
          borderWidth: 0.5,
          "& legend": {
            width: "0px",
            fontWeight: "normal",
            fontSize: "16px",
          },
          // NOTE: // This was causing issues with build, so commented out for now.
          // "&::placeholder": { color: colors.marbleGrey, fontSize: 16 },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.darkGrey,
            borderWidth: 0.5,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderWidth: 0.5,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 0.5,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderWidth: 0.5,
            color: colors.black,
          },
          "&.Mui-error": {
            backgroundColor: colors.white,
            boxShadow: "none",
            borderWidth: 0.5,
          },
          "&.Mui-focused": {
            backgroundColor: colors.white,
            boxShadow: "none",
            borderWidth: 0.5,
          },
        },
      },
    },

    // ---- AUTOCOMPLETE ----
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          width: "100%",
          paddingTop: "0px !important",
          paddingBottom: "0px !important",
          '&&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input': {
            width: "100%",
            padding: "11px 10px",
          },
          "& .MuiAutocomplete-endAdornment": {
            left: "unset",
          },
        },
        root: {
          width: "100%",
        },
      },
    },

    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: colors.black,
        },
      },
    },

    // ---- LABELS ----
    MuiInputLabel: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          color: colors.disable,
          direction: "ltr",
          width: "fit-content",
        },
        outlined: {
          transform: "translate(-10px, 13px) scale(1)",
          "& .MuiFormLabel-asterisk": {
            color: colors.mazdaRed,
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(calc(100% * 0.25), -17px) scale(0.75)",
            width: "calc(100% / 0.75)",
            textAlign: "right",
            right: 0,
            left: "unset",
            maxWidth: "unset",
            color: colors.disable,
          },
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          flexBasis: "100%",
          color: colors.disable,
          fontWeight: "normal",
          fontSize: "16px",
          lineHeight: 1.14,
          left: "unset",
          right: 0,

          "&.Mui-focused": {
            color: colors.disable,
            "& .MuiFormLabel-asterisk": {
              color: colors.mazdaRed,
            },
          },
          "&.Mui-error": {
            color: colors.disable,
          },
        },
        asterisk: {
          marginLeft: "1px",
          verticalAlign: "top",
          color: colors.mazdaRed,
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          alignItems: "flex-start",
          ".MuiFormGroup-root &": {
            display: "inline-block",
            marginRight: "0",
            marginLeft: "0",
            "& .MuiButtonBase-root": {
              display: "none",
            },
          },
        },
      },
    },

    // ---- BUTTON BASE ----
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            border: "2px dashed white",
            outline: "3px dashed #0e3487",
          },
        },
      },
    },

    // ---- FORM HELPER TEXT ----
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 12,
          marginTop: "3px",
          marginRight: "0px !important",
          marginLeft: "0px !important",
          letterSpacing: "0.4px",
          "&.Mui-error": {
            color: colors.mazdaRed,
            textAlign: "start",
          },
        },
      },
    },

    // ---- MENU ----
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: colors.white,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          background: "white",
          fontSize: 15,
          paddingRight: 16,
          paddingLeft: 16,
          paddingTop: 4,
          paddingBottom: 4,
          minHeight: "25px;",
          "&:hover": {
            background: "rgba(247, 243, 235, 0.4);",
          },
          "&.Mui-selected": {
            background: colors.flour,
          },
          "&.Mui-selected:hover": {
            background: "rgba(247, 243, 235, 0.4);",
          },
        },
      },
    },

    // ---- AVATAR ----
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 24,
          height: 24,
          marginBottom: "0 !important",
        },
        img: {
          objectFit: "contain",
        },
      },
    },

    // NOTE:
    // I intentionally did NOT migrate the old JSS `$checked`-style
    // overrides for Switch / Checkbox / Radio because those rely on
    // v4 JSS syntax that doesn't exist in v7.
    //
    // For those, best approach now:
    // - use styleOverrides with "&.Mui-checked" selectors OR
    // - use styleOverrides callbacks with ownerState + sx on specific instances.
  },
}

// Create MUI theme with locale and then extend with custom fields
const baseTheme = createTheme(muiThemeOptions, heIL)

const theme = {
  ...baseTheme,
  colors,
  boxShadows,
}

export default theme
export type Theme = typeof theme
